import MarkerUtils from "@/helpers/markerUtils"
import LiikenneUtils from "@/helpers/liikenneUtils"
import AreaUtils from "@/helpers/areaUtils"
import LegendLimitsUtils from "@/helpers/legendLimitsUtils"
import ApiDataModel, { GeometryData } from "@/models/apidatamodel"
import L from "leaflet"
import moment from "moment"
import DynamicRightSlotData from "@/models/dynamicrighslotdata"
import { AreaTypes } from "@/helpers/enums"
import TableData from "@/models/tabledata"
import utils from "../utils/utils"
import { store } from '@/store/store'
import i18n from '@/i18n'

const t = i18n.global.t

export default class VesiliikenneService {
    MarkerUtils = new MarkerUtils(store.getters.contentServiceUrl, store.getters.contentServiceTimeStamp)
    LiikenneUtils = new LiikenneUtils()
    AreaUtils = new AreaUtils()
    LegendLimitsUtils = new LegendLimitsUtils()

    rekisteroidytVesikulkuneuvotLegendLimits = this.LegendLimitsUtils.getLegendLimits("RekisteroidytVesikulkuneuvot")

    get currentLanguage() {
        return store.getters.activeLang
    }

    /** Haetun datan käsittelyä: Rekisteröintitoimipaikat
     eri värisiä markereita riippuen AliTyypistä / legenda näytettävä / tooltip tyypin mukaan
     rightslotissa näytetään koska tiedot on päivitetty sekä kartalla näkyvien toimipisteiden määrä, käsitellään map.vue ja piirron lopuksi
     TODO: Kartan näkyvä alue tieto vielä harkinnassa*/
    async handleDataRekisterointitoimipaikat(data): Promise<ApiDataModel[]> {
        try {
            const apiData = new Array<ApiDataModel>()
            let updatedText = ""

            data.forEach((value) => {
                const markerOptions = {
                    id: value.id,
                    type: value.alityyppi,
                    riseOnHover: true,
                    icon: L.icon({
                        iconUrl: this.getToimipaikkaTyyppiIcon(value.alityyppi),
                        iconAnchor: [12, 40],
                        iconSize: [24, 40],
                        popupAnchor: [0, -30]
                    })
                }
                value.koordinaatit.markerOptions = markerOptions

                let popup = "<p style='margin: 0px;'><b>" + value.nimi + "</b></p>"
                popup += "<div>" + value.katuosoite + "<br/>"
                + value.postinro + " " + value.postitoimipaikka?.toUpperCase() + "<br/>"

                popup += "<a class='popuplink' target='_blank' href='" + value.info + "'>" + this.createPopUpLinkAriaLabel(value.nimi)
                    + " <img class='iframe-external-icon' src='" + this.getExternalLink() + "' alt='"
                    + this.getExternalLinkAlt() + "'>" + utils.getLinkOpensNewTabSpan() + "</a>"

                popup += "</div>"
                value.popup = popup

                if (updatedText.length === 0) {
                    updatedText = value.julkaisuaika
                }

                apiData.push(new ApiDataModel(value.id.toString(), true, value.koordinaatit, value, null, null))
            })

            if (data.length > 0) {
                // Tiedot päivitetty teksti
                const rightSlotData = new DynamicRightSlotData(data[0].id.toString())
                rightSlotData.dataUpdatedDate = this.getUpdatedtext(data[0].julkaisuaika)
                store.dispatch("setRightSlotData", rightSlotData)
            }

            // lisätään aineistoa kuvaava tieto "Tiedot päivitetty" tekstiin
            const rightSlotData = new DynamicRightSlotData(data[0].id.toString())

            if (updatedText.length > 0) {
                rightSlotData.dataUpdatedDate = this.getUpdatedtext(updatedText)
            }

            store.dispatch("setRightSlotData", rightSlotData)

            return apiData
        }
        catch (err) {
            return new Array<ApiDataModel>()
        }
    }

    async handleDataRekisteroidytVesikulkuneuvot(data): Promise<ApiDataModel[]> {
        try {
            this.LiikenneUtils.updateJulkaisuvuosi(data)
            const areaType = this.AreaUtils.getAreaTypeFromData(data)
            // Haetaan ensin kuntien/maakuntien rajat storesta
            const models = this.AreaUtils.getAreas(areaType, false)

            // Lisätään sitten RightSlotData ja päivitetään kartalla näytettävien alueiden värit
            data.forEach((value) => {
                let id: string = "0"
                // Kartalla näytettävä väri
                const color = this.getRekisteroidytVesikulkuneuvotLegendColor(value.suhteellinen)
                if (areaType === AreaTypes.Municipality) {
                    id = value.kuntanumero ?? "0"
                }
                else if (areaType === AreaTypes.Province) {
                    id = value.maakuntanumero ?? "0"
                }

                const rightSlotData = new DynamicRightSlotData(id)
                rightSlotData.dataUpdatedDate = this.getUpdatedtext(value.julkaisuaika ?? moment().toISOString()) ?? "?"

                const tableValues: Array<Array<string>> = []
                tableValues.push([
                    value.lukumaara?.toString(),
                    value.erotusprosentuaalinen != null ? value.erotusprosentuaalinen?.toString() + "%" : ""
                ])
                rightSlotData.tableData = new TableData(this.RekisteroidytVesikulkuneuvotTableColumnHeaders, tableValues, null)

                if (areaType === AreaTypes.Country) {
                    rightSlotData.municipality = t("App.TieLiikenne.EntireFinland") ?? ""
                    store.dispatch("setRightSlotData", rightSlotData)
                }
                else {
                    rightSlotData.municipality = this.LiikenneUtils.getName(value)
                }

                // Päivitetään tiedot modeleihin
                this.updateModels(models, rightSlotData, color, id)
            })
            return models
        }
        catch (err) {
            console.log(err)
            return new Array<ApiDataModel>()
        }
    }

    getExternalLink(): string {
        return store.getters.contentServiceUrl + "/Content/icons/svg-blue/linkki-external.svg" + "?" + store.getters.contentServiceTimeStamp
    }

    getExternalLinkAlt(): string | undefined {
        return t("App.Link.UlosJohtavaLinkki")
    }

    updateModels(models: ApiDataModel[], rightSlotData: DynamicRightSlotData, color: string, id: string) {
        const item = models.find(e => e.id === id)
        if (item !== undefined) {
            item.rightSlotData = rightSlotData
            item.featureData.koordinaatit.options.color = color
            item.featureData.koordinaatit.options.fillColor = color
            item.featureData.koordinaatit.options.fillOpacity = color === "" ? 0 : 0.8 // jos ei ole väriä niin täysi läpinäkyvyys
            if (item.geometryData !== null) {
                // Vain ne alueet voi valita kartalta, joilla on dataa
                item.geometryData.handleOnClick = true
            }
        }
    }

    getUpdatedtext(time: string): string {
        const timestring = moment(time).format("MM/YYYY") ?? "?"
        return t("App.VesiLiikenne.DataUpdated") + " " + timestring
    }

    getToimipaikkaTyyppiIcon(tyyppi: string) {
        if (tyyppi.toLowerCase() === "yleisetpisteet") {
            return this.MarkerUtils.createTurquoiseMarker()
        }
        else {
            return this.MarkerUtils.createPurpleMarker()
        }
    }

    getToimipaikkaTyyppiLegend(): string {
        const text1 = t("App.VesiLiikenne.Legend.yleisetpisteet") ?? ""
        const text2 = t("App.VesiLiikenne.Legend.omatrekisteroinnit") ?? ""
        let legend = "<div class=\"legend-item\"><div><img alt=\"\" src= " + this.getToimipaikkaTyyppiIcon("yleisetpisteet") + "\"></div><div>" + text1 + "</div></div>"
        legend += "<div class=\"legend-item\"><div><img alt=\"\" src= " + this.getToimipaikkaTyyppiIcon("omatrekisteroinnit") + "\"></div><div>" + text2 + "</div></div>"

        return legend
    }

    getRekisteroidytVesikulkuneuvotLegendColor(value: number): string {
        return this.LiikenneUtils.getLegendColor(value, this.rekisteroidytVesikulkuneuvotLegendLimits)
    }

    getRekisteroidytVesikulkuneuvotLegend(): string {
        const limits = this.rekisteroidytVesikulkuneuvotLegendLimits
        if (limits.length === 0) {
            return ""
        }

        const unit = t("App.Legend.RekisteroidytVesikulkuneuvot") ?? ""
        let legend = "<div class=\"legend-item\"><i style=\"background: " + this.getRekisteroidytVesikulkuneuvotLegendColor(0) + "\"></i><div>" + t("App.TieLiikenne.Legend.TietoEiSaatavilla") + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getRekisteroidytVesikulkuneuvotLegendColor(limits[0] - 1) + "\"></i><div>" + "<" + limits[0] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getRekisteroidytVesikulkuneuvotLegendColor(limits[1]) + "\"></i><div>" + limits[0] + "-" + limits[1] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getRekisteroidytVesikulkuneuvotLegendColor(limits[2]) + "\"></i><div>" + limits[1] + "-" + limits[2] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getRekisteroidytVesikulkuneuvotLegendColor(limits[3]) + "\"></i><div>" + limits[2] + "-" + limits[3] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getRekisteroidytVesikulkuneuvotLegendColor(limits[4]) + "\"></i><div>" + limits[3] + "-" + limits[4] + " " + unit + "</div></div>"
        legend += "<div class=\"legend-item\"><i style=\"background: " + this.getRekisteroidytVesikulkuneuvotLegendColor(limits[4] + 1) + "\"></i><div>" + ">" + limits[4] + " " + unit + "</div></div>"

        return legend
    }

    get RekisteroidytVesikulkuneuvotTableColumnHeaders(): string[] {
        const columns: string[] = []
        columns.push(t("App.Tieliikenne.RightSlot.Maara") ?? "App.Tieliikenne.RightSlot.Maara")
        columns.push(t("App.Tieliikenne.RightSlot.Vuosimuutos") ?? "App.Tieliikenne.RightSlot.Vuosimuutos")
        return columns
    }

    createPopUpLinkAriaLabel(name: string) {
        return t("App.Vesiliikenne.Popup.PalvelupisteenVerkkosivuilta") + " " + name
    }
}

export interface Rekisterointitoimipaikat {
    id: number;
    tyyppi: string;
    alityyppi: string;
    julkaisuaika: string;
    nimi: string;
    katuosoite: string | null;
    postinro: string | null;
    postitoimipaikka: string | null;
    info: string | null;

    koordinaatit: GeometryData;
    popup: string;
}
